import React, { useEffect, useState } from 'react';

import lodash from 'lodash';
import moment from 'moment';
import { connect } from 'react-redux';

import { Box, Typography } from '@material-ui/core';

import { replaceTimezone, toISOLocalWithTz } from 'common/util/dateTime';

import { retrieveStudyAuditLogData } from 'pages/delegationOfAuthority/v2/actions/studyAuditLogV2';

import styles from './AuditHistory.scss';
import AuditHistoryItem from './AuditHistoryItem';

const auditLabel = {
    EDIT_DELEGATION: 'MODIFIED DELEGATION',
    CREATE_DELEGATION: 'CREATED DELEGATION',
    SIGN_DELEGATION_AS_PI: 'SIGNED DELEGATION',
    SIGN_DELEGATION_AS_STAFF: 'SIGNED DELEGATION',
};

const fieldsNeedToCompare = [
    'tasks',
    'roleName',
    'startDate',
    'endDate',
    'signatureExpectations',
    'defaultSignatureExpectations',
];

const requestSig = 'signatureExpectations';
const defaultSig = 'defaultSignatureExpectations';

const reduceAuditLogData = (dataFiltered) => {
    return dataFiltered.reduce((total, cur, index) => {
        if (cur.actionType.includes('EDIT')) {
            const prevItem = dataFiltered[index - 1] ? dataFiltered[index - 1].value : {};
            let [removed, added, requestedSignature, originalSignature] = [{}, {}, {}, {}];

            const getSignatureExpectations = lodash.get(cur.value, requestSig);
            if (!lodash.isEmpty(getSignatureExpectations)) {
                requestedSignature.requestedSignature = getSignatureExpectations;
            }

            const getOriginalSignatures = lodash.get(cur.value, defaultSig);
            if (!lodash.isEmpty(getOriginalSignatures)) {
                originalSignature.originalSignature = getOriginalSignatures;
            }

            fieldsNeedToCompare.forEach((fieldName) => {
                let fieldPrevItem = lodash.get(prevItem, fieldName);
                fieldPrevItem =
                    typeof fieldPrevItem === 'string' && !lodash.isNil(fieldPrevItem)
                        ? [fieldPrevItem]
                        : fieldPrevItem;

                let fieldItem = lodash.get(cur.value, fieldName);
                fieldItem =
                    typeof fieldItem === 'string' && !lodash.isNil(fieldItem)
                        ? [fieldItem]
                        : fieldItem;

                const dataAdded = lodash.differenceWith(fieldItem, fieldPrevItem, lodash.isEqual);
                if (!lodash.isEmpty(dataAdded)) {
                    added[fieldName] = dataAdded;
                }

                const dataRemoved = lodash.differenceWith(fieldPrevItem, fieldItem, lodash.isEqual);
                if (!lodash.isEmpty(dataRemoved)) {
                    removed[fieldName] = dataRemoved;
                }
            });

            if (lodash.isEmpty(lodash.get(cur, 'value.defaultSignatureExpectations'))) {
                requestedSignature = lodash.omit(requestedSignature, 'requestedSignature');
                removed = lodash.omit(removed, [defaultSig]);
            }

            if (!lodash.isEmpty(lodash.get(cur, 'value.defaultSignatureExpectations'))) {
                added = lodash.omit(added, [requestSig, defaultSig]);
                removed = lodash.omit(removed, [requestSig, defaultSig]);
            }

            // Handle for case study type
            if (lodash.get(cur.value, 'origin') === 'Study Settings') {
                originalSignature = lodash.omit(originalSignature, ['originalSignature']);
            }

            if (lodash.get(cur.value, 'origin') === 'Admin') {
                originalSignature = lodash.omit(originalSignature, ['originalSignature']);
            }

            if (lodash.isEmpty(requestedSignature.requestedSignature)) {
                requestedSignature = lodash.omit(requestedSignature, 'requestedSignature');
            }

            if (lodash.isEmpty(lodash.get(cur, 'value.taskIds'))) {
                removed = lodash.omit(removed, [requestSig]);
                added = lodash.omit(added, [requestSig]);
                requestedSignature = lodash.omit(requestedSignature, 'requestedSignature');
            }

            return total.concat({ ...cur, added, removed, requestedSignature, originalSignature });
        }
        return total.concat(cur);
    }, []);
};

const formatAuditLogData = (dataAPI, timezone, doaRoles) => {
    if (!dataAPI || !doaRoles) return;

    for (let index = 0; index < dataAPI.length; index++) {
        let valueAudit = dataAPI[index].value;
        let parseValue = typeof valueAudit === 'string' ? JSON.parse(valueAudit) : valueAudit;

        if (dataAPI[index].actionType.includes('SIGN')) {
            let prevValueAudit = dataAPI[index - 1].value;
            const signOrigin = parseValue.origin;
            dataAPI[index].value = lodash.omit({ ...parseValue, ...prevValueAudit }, [
                defaultSig,
                requestSig,
            ]);
            if (signOrigin !== undefined) {
                dataAPI[index].value.origin = signOrigin;
            }
        } else {
            dataAPI[index].value = parseValue;
        }

        dataAPI[index].date = moment(
            replaceTimezone(toISOLocalWithTz(new Date(dataAPI[index].createdAt), timezone))
        ).format('MMM DD, YYYY');
    }

    const filterAudit = dataAPI.map((audit) => {
        return {
            ...audit,
            label: auditLabel[audit.actionType],
            value: {
                ...audit.value,
                roleName: lodash.get(
                    doaRoles.find((role) => role.id === audit.value.roleManagementId),
                    'name'
                ),
                tasks: lodash.get(audit, 'value.tasks', []).map(({ taskIndex, ...props }) => props),
            },
        };
    });

    const reduceAudit = reduceAuditLogData(filterAudit);
    const groupByAudit = lodash.groupBy(reduceAudit, 'date');

    const orderDescAudit = lodash(groupByAudit)
        .toPairs()
        .orderBy((item) => moment(item[0]), ['desc'])
        .fromPairs()
        .value();

    return orderDescAudit;
};

const getTestaments = (audits, auditId) => {
    if (audits.length === 0) return;
    const audit = audits.find((item) => item.id === auditId);
    if (!audit) return;
    if (typeof audit.value !== 'string') return;
    const testament = lodash.get(JSON.parse(audit.value), 'testament', '');

    return testament;
};

function AuditHistory(props) {
    const {
        binderId,
        doaRoles,
        isStudyType,
        auditHistory,
        delegationName,
        retrieveStudyAuditLogData,
        institutionTimezone,
        institutionTimezoneName,
        institutionId,
        customStyling
    } = props;
    const [isExpandAll, setExpandAll] = useState(false);
    const [dataListItem, setDataListItem] = useState([]);
    const [toggleList, setToggleList] = useState([]);

    useEffect(() => {
        retrieveStudyAuditLogData(binderId, null, institutionId);
    }, [binderId]);

    useEffect(() => {
        setDataListItem(
            formatAuditLogData(lodash.cloneDeep(auditHistory), institutionTimezone, doaRoles)
        );
    }, [auditHistory, doaRoles]);

    useEffect(() => {
        if (!lodash.isEmpty(auditHistory) && toggleList.length === auditHistory.length) {
            setExpandAll(true);
        }
        if (!lodash.isEmpty(auditHistory) && lodash.isEmpty(toggleList)) {
            setExpandAll(false);
        }
    }, [toggleList, auditHistory]);

    const handleExpandAll = () => {
        setExpandAll(!isExpandAll);
    };

    const handleExpandItem = (index) => {
        if (!toggleList.includes(index)) {
            setToggleList([...toggleList, index]);
            return;
        }

        const updatedArr = lodash.difference(toggleList, [index]);
        setToggleList(updatedArr);
    };

    return (
        <div className={styles.AuditHistory} style={customStyling}>
            <Box
                display="flex"
                justifyContent="space-between"
                my={4}
                marginLeft={3}
                marginRight={4}
            >
                <Typography variant="body1">Audit History</Typography>
                <Typography
                    variant="body1"
                    color="primary"
                    style={{ cursor: 'pointer' }}
                    onClick={handleExpandAll}
                    data-testid="Audit_ExpandAll"
                >
                    <span>{isExpandAll ? 'Collapse' : 'Expand'} History</span>
                </Typography>
            </Box>
            <Box>
                {lodash.keys(dataListItem).map((item, index) => {
                    const auditsSortByDate = lodash.orderBy(
                        dataListItem[item],
                        ['createdAt'],
                        'desc'
                    );

                    return (
                        <Box className={styles.AuditHistory__item} key={index}>
                            <Box className={styles.AuditHistory__header}>
                                <Typography variant="body1">{item}</Typography>
                            </Box>

                            {lodash.values(auditsSortByDate).map((audit, indexAudit) => {
                                return (
                                    <AuditHistoryItem
                                        index={indexAudit}
                                        data={audit}
                                        key={audit.id}
                                        open={isExpandAll}
                                        isStudyType={isStudyType}
                                        onClickExpand={handleExpandItem}
                                        delegationName={delegationName}
                                        institutionTimezone={institutionTimezone}
                                        institutionTimezoneName={institutionTimezoneName}
                                        testament={getTestaments(auditHistory, audit.id)}
                                    />
                                );
                            })}
                        </Box>
                    );
                })}
            </Box>
        </div>
    );
}

export default connect(
    (state, props) => {
        const doaRoles = lodash.get(state, 'doaRoles', []);
        const auditEvents = lodash.get(state, 'doaAuditHistory', []);
        const auditHistory = lodash.get(auditEvents, props.delegationId, []);

        const me = lodash.get(state, 'me', {});
        const institutionTimezone = lodash.get(me, 'institutionTimezone', 'Z');
        const institutionTimezoneName = lodash.get(me, 'institution.properties.Timezone.value', 'US/Eastern');

        return {
            doaRoles,
            auditHistory,
            institutionTimezone,
            institutionTimezoneName,
        };
    },
    {
        retrieveStudyAuditLogData,
    }
)(AuditHistory);